import { fmtNum } from "@/lib/format";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { IconName } from "@/components/ui/Icon";
import { useAuth } from "@/hooks/useAuth";
import { useNotificationSettings } from "@/hooks/useNotificationSettings";
import { useSalesSyncStatus } from "@/hooks/useSalesInvoices";
import { useRqcDriftStatus } from "@/hooks/useRqcDriftStatus";
import { usersRepo } from "@/services/users.repository";
import { dailyShipmentsRepo } from "@/services/daily_shipments.repository";
import type { DailyShipment } from "@/domain/daily_shipments";
import { kstToday } from "@/domain/rqc";
import { SpaceContext } from "@/contexts/space-context";
import { LEGACY_TENANT, isTenantDataConnected } from "@/domain/space";
import { menuPath } from "@/domain/menus";

/** 영업·출하 알림 데이터(daily_shipments·sales_sync·rqc_drift)가 속한 법인 — 지금은 중앙만 연결 */
const CENTRAL = LEGACY_TENANT;
const SHIP_LINK = menuPath("dispatch-view", CENTRAL);

/** 현재 KST 시(0~23) — 마감(ship_eod) 17시 경계 판정용 */
function kstHour(): number {
  return Number(
    new Date().toLocaleString("en-GB", { timeZone: "Asia/Seoul", hour: "2-digit", hour12: false }),
  );
}
const fmtM3 = (n: number) => `${fmtNum(n, 1)}㎥`;

/**
 * 상단바 알림 데이터 훅 — 실데이터 3종을 표시 설정/권한으로 게이팅해 합침.
 *
 *  - signup     : 가입 승인 대기. users 구독(그룹 미지정+비활성). 관리자만.
 *  - sales_sync : 영업 자동 동기화 "실패"만 알림(성공/상태는 판매 화면에서 확인). sales.view.
 *  - rqc_drift  : 출하량 불일치 감지. 최신 rqc_drift_log. shipment.view || dispatch.view.
 *
 * 각 타입은 통합관리 › 알림(/group/notify, system_settings/notification_settings)의
 * on/off 와 사용자 권한을 모두 만족할 때만 표시. 표시할 이벤트가 없으면 항목 없음(점 꺼짐).
 *
 * 법인 섞임 방지(2026-10-20 1단계): 영업·출하 알림은 중앙 데이터라 "중앙 공간" 또는
 * "내 업무 + 중앙 소속"일 때만 구독한다. 경원 공간에서는 구독 자체를 끈다(가입 승인은 그룹 공통이라 유지).
 * 링크는 데이터가 속한 법인 주소(/c/joongang/…)로 고정.
 */

export type NotifTone = "info" | "warn" | "success" | "danger";

export interface NotifItem {
  id: string;
  type: string;
  icon: IconName;
  tone: NotifTone;
  title: string;
  sub: string;
  time: string;
  read: boolean;
  navTo?: string;
}

/** 가입 신청(미승인) 판별 — 그룹 미지정 + 비활성 (UsersSection.isPending 과 동일) */
const isPending = (r: { role?: string | null; active?: boolean; deleted?: boolean }) => !r.deleted && !r.role && !r.active;

/** Date → 상대시각 표시 */
function relTime(d: Date | null): string {
  if (!d) return "";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "방금 전";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}분 전`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}시간 전`;
  const day = Math.floor(hr / 24);
  if (day === 1) return "어제";
  if (day < 7) return `${day}일 전`;
  return d.toLocaleDateString("ko-KR", { month: "2-digit", day: "2-digit" });
}

export function useNotifications() {
  const { user, hasPerm } = useAuth();
  const isAdmin = !!user?.isAdmin;
  const { settings } = useNotificationSettings();
  // 공간(SpaceProvider 밖이면 null → 옛 동작 그대로)
  const sp = useContext(SpaceContext);
  const centralOn =
    !sp ||
    (sp.space === "my"
      ? sp.tenants.includes(CENTRAL) && isTenantDataConnected(CENTRAL)
      : sp.tenant === CENTRAL && isTenantDataConnected(CENTRAL));

  // 각 소스 활성 여부 = 표시설정 ON && 권한 보유 (영업·출하는 + 중앙 공간)
  const signupOn = settings.signup && isAdmin;
  const salesOn = centralOn && settings.sales_sync && hasPerm("sales.view", CENTRAL);
  const shipPerm = centralOn && (hasPerm("shipment.view", CENTRAL) || hasPerm("dispatch.view", CENTRAL));
  const driftOn = settings.rqc_drift && shipPerm;
  // 출하 알림(예정/진행/완료/마감) — 4종 중 하나라도 ON + 출하 권한이면 오늘 출하 구독
  const shipAnyOn =
    shipPerm &&
    (settings.morning_plan || settings.ship_start || settings.ship_finish || settings.ship_eod);

  // ── 소스별 구독 (활성일 때만) ──
  const [pendingCount, setPendingCount] = useState(0);
  useEffect(() => {
    if (!signupOn) { setPendingCount(0); return; }
    const unsub = usersRepo.watch(
      (rows) => setPendingCount(rows.filter(isPending).length),
      () => setPendingCount(0),
    );
    return unsub;
  }, [signupOn]);

  const syncStatus = useSalesSyncStatus(salesOn);
  const drift = useRqcDriftStatus(driftOn);

  // 오늘 출하 집계 구독 (출하 알림 활성 시)
  const today = kstToday();
  const [todayShip, setTodayShip] = useState<DailyShipment | null>(null);
  useEffect(() => {
    if (!shipAnyOn) { setTodayShip(null); return; }
    return dailyShipmentsRepo.watchByDate(today, setTodayShip, () => setTodayShip(null));
  }, [shipAnyOn, today]);

  const [readIds, setReadIds] = useState<Set<string>>(() => new Set());

  const items = useMemo<NotifItem[]>(() => {
    const list: NotifItem[] = [];

    if (signupOn && pendingCount > 0) {
      const id = `signup-${pendingCount}`; // 건수 변하면 새 알림으로 재노출
      list.push({
        id,
        type: "signup",
        icon: "users",
        tone: "warn",
        title: `가입 승인 대기 ${pendingCount}건`,
        sub: "새 사용자가 가입 승인을 기다리고 있습니다",
        time: "확인 필요",
        read: readIds.has(id),
        navTo: "/group/users", // 가입 신청 승인 = 통합관리 › 사용자·계정(목록 위 가입 신청 줄, 12 §8-B)
      });
    }

    // 영업 동기화는 "실패"만 알림 (성공/마지막 시각은 판매 화면에서 확인)
    if (salesOn && syncStatus && syncStatus.ok === false) {
      const id = `sales-sync-fail-${syncStatus.lastRunAt?.getTime() ?? "0"}`;
      list.push({
        id,
        type: "sales_sync",
        icon: "backup",
        tone: "danger",
        title: "영업 데이터 동기화 실패",
        sub: syncStatus.error || "동기화 중 오류가 발생했습니다. 수동 동기화를 시도해 주세요.",
        time: relTime(syncStatus.lastRunAt),
        read: readIds.has(id),
        navTo: menuPath("sales", CENTRAL),
      });
    }

    if (driftOn && drift && drift.driftCount > 0) {
      const id = `rqc-drift-${drift.checkedAt?.getTime() ?? "0"}`;
      const months = drift.driftMonths.join(", ");
      list.push({
        id,
        type: "rqc_drift",
        icon: "truck",
        tone: "warn",
        title: `출하량 불일치 ${drift.driftCount}개월 감지`,
        sub:
          drift.backfillSucceeded > 0
            ? `자동 보정 ${drift.backfillSucceeded}건 완료${months ? ` · ${months}` : ""}`
            : `대상 월: ${months || "—"}`,
        time: relTime(drift.checkedAt),
        read: readIds.has(id),
        navTo: SHIP_LINK,
      });
    }

    // ── 출하 알림 (오늘 daily_shipments 상태 기반) ──
    if (shipPerm && todayShip) {
      const planned = todayShip.total_planned_m3 ?? 0;
      const shipped = todayShip.total_m3 ?? 0;
      const remain = Math.max(0, planned - shipped);

      // 예정/진행/완료는 상태 전이(한 시점에 하나)
      if (settings.morning_plan && shipped <= 0 && planned > 0) {
        const id = `ship-plan-${Math.round(planned)}`;
        list.push({
          id, type: "morning_plan", icon: "calendar", tone: "info",
          title: "오늘 출하 예정", sub: `예정 ${fmtM3(planned)} (관급·사급)`,
          time: "오늘", read: readIds.has(id), navTo: SHIP_LINK,
        });
      } else if (settings.ship_finish && shipped > 0 && planned > 0 && remain <= 0) {
        const id = `ship-finish-${Math.round(shipped)}`;
        list.push({
          id, type: "ship_finish", icon: "check", tone: "success",
          title: "금일 출하 마무리", sub: `총 ${fmtM3(shipped)} 출하 완료`,
          time: "오늘", read: readIds.has(id), navTo: SHIP_LINK,
        });
      } else if (settings.ship_start && shipped > 0) {
        const id = `ship-start-${Math.round(shipped)}`;
        list.push({
          id, type: "ship_start", icon: "truck", tone: "info",
          title: "금일 출하 진행 중", sub: `${fmtM3(shipped)}${planned > 0 ? ` / ${fmtM3(planned)}` : ""} 출하`,
          time: "오늘", read: readIds.has(id), navTo: SHIP_LINK,
        });
      }

      // 마감 결산 — 17시 이후 별도(진행/완료와 함께 뜰 수 있음)
      if (settings.ship_eod && kstHour() >= 17 && shipped > 0) {
        const id = `ship-eod-${Math.round(shipped)}`;
        list.push({
          id, type: "ship_eod", icon: "ledger", tone: "info",
          title: "오늘 출하 마감", sub: `${fmtM3(shipped)} / ${fmtM3(planned)} (잔량 ${fmtM3(remain)})`,
          time: "마감", read: readIds.has(id), navTo: SHIP_LINK,
        });
      }
    }

    return list;
  }, [signupOn, pendingCount, salesOn, syncStatus, driftOn, drift, shipPerm, todayShip, settings, readIds]);

  const unreadCount = useMemo(() => items.filter((i) => !i.read).length, [items]);

  const markRead = useCallback((id: string) => {
    setReadIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setReadIds((prev) => {
      const next = new Set(prev);
      for (const i of items) next.add(i.id);
      return next;
    });
  }, [items]);

  return { items, unreadCount, markRead, markAllRead };
}
